import React, { useEffect } from "react";
import { ensureExhaustive } from "@dhau/lang-extras";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import {
	guestLogin,
	setWhoFor,
	setGiftType,
	clearGiftType,
	clearParsedNameValues,
	clearWhoFor,
	changeEmailAddress,
} from "~/features/user/store-slice.ts";
import { selectUser } from "~/features/user/selectors.ts";
import { useWebsiteUrl } from "~/context/website-url.tsx";
import PurposeSelectionStep from "./purpose-selection-step.tsx";
import GiftTypeSelectionStep from "./gift-type-selection-step.tsx";
import NameCollectionStep from "./name-collection-step.tsx";
import EmailCollectionStep from "./email-collection-step.tsx";
import BackButton from "./back-button.tsx";
import type {
	WhoFor,
	GiftType,
} from "@brickme/project-core/src/model/project.ts";

type GetStartedStep = "purpose" | "giftType" | "name" | "email";

type GetStartedFlowProps = {
	readonly onGuestLogin: () => void;
	readonly skipNameAndEmail?: boolean;
	readonly onComplete?: () => void;
};

function GetStartedFlow({
	onGuestLogin,
	skipNameAndEmail,
	onComplete,
}: GetStartedFlowProps) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(selectUser);
	const websiteUrl = useWebsiteUrl("");

	// Get flow state from Redux
	const whoFor = useAppSelector((state) => state.user.whoFor);
	const giftType = useAppSelector((state) => state.user.giftType);
	const parsedNameValues = useAppSelector(
		(state) => state.user.parsedNameValues,
	);

	// Determine current step based on Redux state
	const getCurrentStep = (): GetStartedStep => {
		if (
			!skipNameAndEmail &&
			(user.type === "waitingForOtp" || user.type === "verifyingOtp")
		) {
			return "email";
		}
		if (!skipNameAndEmail && parsedNameValues) {
			return "email";
		}
		if (giftType && !parsedNameValues && !skipNameAndEmail) {
			return "name";
		}
		if (whoFor === "someone_else" && !giftType) {
			return "giftType";
		}
		if (whoFor === "myself" && !parsedNameValues && !skipNameAndEmail) {
			return "name";
		}
		return "purpose";
	};

	// Detect flow completion
	useEffect(() => {
		// Centralized completion logic
		const isFlowComplete = () => {
			if (skipNameAndEmail) {
				if (whoFor === "myself") return true;
				if (whoFor === "someone_else" && giftType === "brick_mosaic")
					return true;
				// gift card handled by redirect
			} else {
				return user.type === "guest" || user.type === "signedIn";
			}
			return false;
		};

		if (isFlowComplete()) {
			onComplete?.();
		}
	}, [skipNameAndEmail, whoFor, giftType, user.type, onComplete]);

	const currentStep = getCurrentStep();

	const setPurpose = (purpose: WhoFor) => {
		dispatch(setWhoFor(purpose));
		// No direct onComplete call here
	};

	const handleGiftTypeSelect = (giftType: GiftType) => {
		if (giftType === "gift_card") {
			// Redirect to Shopify gift card page immediately without updating Redux
			window.location.href = `${websiteUrl}/collections/gift-cards`;
			return;
		}
		// Only update Redux state for brick mosaic (which continues the flow)
		dispatch(setGiftType(giftType));
		// No direct onComplete call here
	};

	const handleGuestLogin = () => {
		dispatch(guestLogin());
		onGuestLogin();
	};

	const handleBack = () => {
		switch (currentStep) {
			case "purpose":
				// Go to website URL instead of browser back
				window.location.href = websiteUrl;
				break;
			case "giftType":
				// Go back to purpose selection by clearing whoFor
				dispatch(clearWhoFor());
				break;
			case "name":
				// If we have a gift type, go back to gift type selection
				// Otherwise go back to purpose selection
				if (giftType) {
					dispatch(clearGiftType());
				} else {
					dispatch(clearWhoFor());
				}
				break;
			case "email":
				// If we're in OTP verification, go back to email input
				// Otherwise go back to name collection
				if (user.type === "waitingForOtp" || user.type === "verifyingOtp") {
					dispatch(changeEmailAddress());
				} else {
					dispatch(clearParsedNameValues());
				}
				break;
			default:
				ensureExhaustive(currentStep);
		}
	};

	const renderCurrentStep = () => {
		switch (currentStep) {
			case "purpose":
				return <PurposeSelectionStep onPurposeSelect={setPurpose} />;
			case "giftType":
				return (
					<GiftTypeSelectionStep onGiftTypeSelect={handleGiftTypeSelect} />
				);
			case "name":
				if (skipNameAndEmail) return null;
				return <NameCollectionStep />;
			case "email":
				if (skipNameAndEmail) return null;
				return (
					<EmailCollectionStep
						previouslyCollectedName={
							parsedNameValues
								? `${parsedNameValues.firstName} ${parsedNameValues.middleName || ""} ${parsedNameValues.lastName || ""}`.trim()
								: undefined
						}
						onGuestLogin={handleGuestLogin}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<>
			<BackButton onBack={handleBack} show={true} />
			{renderCurrentStep()}
		</>
	);
}

export default GetStartedFlow;
